import React, { useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useCategories } from "@/context/CategoriesContext";
import { adminCreateCategory, adminUpdateCategory, adminDeleteCategory } from "@/lib/api";

const inputCls = "w-full border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-foreground";

const CategoriesAdmin = () => {
  const { categories } = useCategories();
  const qc = useQueryClient();
  const [rows, setRows] = useState(null);
  const [nRo, setNRo] = useState("");
  const [nEn, setNEn] = useState("");
  const list = rows || categories;

  const refresh = async () => { await qc.invalidateQueries({ queryKey: ["categories"] }); };

  const add = async () => {
    if (!nRo.trim() || !nEn.trim()) { toast.error("Completează numele RO și EN"); return; }
    try {
      const c = await adminCreateCategory({ ro: nRo.trim(), en: nEn.trim() });
      setRows([...(list), c]); setNRo(""); setNEn(""); await refresh(); toast.success("Categorie adăugată");
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const rename = async (slug, ro, en) => {
    try { await adminUpdateCategory(slug, { ro, en }); await refresh(); toast.success("Salvat"); }
    catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const del = async (slug) => {
    if (!window.confirm("Ștergi categoria?")) return;
    try { await adminDeleteCategory(slug); setRows(list.filter((c) => c.slug !== slug)); await refresh(); toast.success("Ștearsă"); }
    catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const setLocal = (slug, k, v) => setRows(list.map((c) => (c.slug === slug ? { ...c, [k]: v } : c)));

  return (
    <div className="p-5 sm:p-8" data-testid="categories-admin">
      <h2 className="mb-2 text-xl uc font-semibold">Categorii</h2>
      <p className="mb-6 font-mono text-[10px] uppercase leading-relaxed tracking-widest text-muted-foreground">
        Adaugă, redenumește sau șterge categorii. O categorie cu produse nu poate fi ștearsă până nu muți produsele.
      </p>

      <div className="space-y-2">
        {list.map((c) => (
          <div key={c.slug} className="flex flex-wrap items-center gap-2 border border-border p-3" data-testid={`category-row-${c.slug}`}>
            <span className="min-w-[90px] font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{c.slug}</span>
            <input className={inputCls + " max-w-[200px]"} value={c.ro} onChange={(e) => setLocal(c.slug, "ro", e.target.value)} data-testid={`category-ro-${c.slug}`} />
            <input className={inputCls + " max-w-[200px]"} value={c.en} onChange={(e) => setLocal(c.slug, "en", e.target.value)} data-testid={`category-en-${c.slug}`} />
            <button onClick={() => rename(c.slug, c.ro, c.en)} data-testid={`category-save-${c.slug}`} className="border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-widest hover:border-foreground">Salvează</button>
            <button onClick={() => del(c.slug)} data-testid={`category-remove-${c.slug}`} className="font-mono text-xs text-destructive">×</button>
          </div>
        ))}
      </div>

      <div className="mt-6 border-t border-border pt-6">
        <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Categorie nouă</div>
        <div className="flex flex-wrap items-center gap-2">
          <input className={inputCls + " max-w-[200px]"} placeholder="Nume RO (ex: Genți)" value={nRo} onChange={(e) => setNRo(e.target.value)} data-testid="new-category-ro" />
          <input className={inputCls + " max-w-[200px]"} placeholder="Name EN (ex: Bags)" value={nEn} onChange={(e) => setNEn(e.target.value)} data-testid="new-category-en" />
          <button onClick={add} data-testid="new-category-add" className="bg-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest text-white hover:opacity-85">+ Adaugă categorie</button>
        </div>
      </div>
    </div>
  );
};

export default CategoriesAdmin;
